import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../../components/Toast';
import ErrorBanner from '../../components/ErrorBanner';
import useUrlState from '../../hooks/useUrlState';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { colors, radius, shadows } from '../../styles/theme';
import { searchMatch } from '../../utils/searchMatch';
import { normalizeDecimalInput, parseDecimal } from '../../utils/decimalInput';
import { hesaplaCariKpi } from '../../utils/cariKpi';
import { logError } from '../../utils/logError';
import { parseTutarTR, formatTutarTR } from '../../utils/para';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ISO tarih (YYYY-MM-DD veya tam ISO) → dd.MM.yyyy. Geçersiz/boş ise olduğu gibi
// döner (tarih-only string; new Date timezone kayması yaşamamak için manuel parse).
const fmtTarih = (iso) => {
  if (!iso || typeof iso !== 'string') return iso || '-';
  const m = iso.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

// KREDI_KARTI_OPTIONS artık DB'den (/api/kredi-kartlari) geliyor — Ayarlar > Kredi Kartları'ndan yönetilir.
// Fallback (API erişilemezse): seed verisi.
const KREDI_KARTI_OPTIONS_FALLBACK = [
  { value: 'KART_9577', label: '9577' },
  { value: 'KART_0662', label: '0662' },
  { value: 'KART_5238', label: '5238' },
  { value: 'KART_1922', label: '1922' },
  { value: 'KART_0965', label: '0965' },
];

// Ödeme yöntemleri artık DB'den (/api/odeme-yontemleri) geliyor — Ayarlar > Ödeme Yöntemleri'nden yönetilir.
// Fallback (API erişilemezse, Anayasa M.1): seed verisiyle birebir.
const ODEME_YONTEMLERI_FALLBACK = [
  { value: 'NAKIT', label: 'Nakit' },
  { value: 'HAVALE', label: 'Havale / EFT' },
  { value: 'KREDI_KARTI', label: 'Kredi Kartı', kart_ister: true },
  { value: 'CEK', label: 'Çek', cek_ister: true },
  { value: 'SENET', label: 'Senet', cek_ister: true },
  { value: 'ACIK_HESAP', label: 'Açık Hesap' },
  { value: 'IADE', label: 'İade', stok_etkisi: 'IADE' },
  { value: 'FIYAT_FARKI', label: 'Fiyat Farkı' },
];

const CariHesaplarPage = () => {
  const [tedarikciler, setTedarikciler] = useState([]);
  const [faturalar, setFaturalar] = useState([]);
  const [odemeler, setOdemeler] = useState([]);
  const [giderBorclar, setGiderBorclar] = useState([]);
  // Cari ekstre — TEK KAYNAK: backend /suppliers/{id}/ekstre döner
  // { net_bakiye, hareketler: [...bakiye_sonrasi...] }. Satır bakiyesi + rozet
  // artık backend'de tek hesaptan; frontend yalnız gösterir (ayrışma imkânsız).
  const [ekstreByTedarikci, setEkstreByTedarikci] = useState({}); // { [id]: {net_bakiye, hareketler} }
  const [faturaKalemler, setFaturaKalemler] = useState({}); // { [faturaId]: { loading, data, error } }
  const faturaKalemlerRef = useRef({}); // lazy-fetch cache guard (çift fetch önle)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // kritik fetch hatası — ErrorBanner (#259 deseni)
  const [ekstreError, setEkstreError] = useState(null); // seçili tedarikçinin ekstre fetch hatası
  const [selectedTedarikci, setSelectedTedarikci] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeTab, setActiveTab] = useState('hareketler'); // hareketler | odeme
  const [odemeForm, setOdemeForm] = useState({ tutar: '', yontem: 'HAVALE', aciklama: '', tarih: new Date().toISOString().split('T')[0], cek_no: '', cek_vade_tarihi: '', odeme_detay: '', firma: '' });
  const [odemeSaving, setOdemeSaving] = useState(false);
  // İade (tedarikçi iadesi) akışı state'i — C5
  const [iadeBelgeler, setIadeBelgeler] = useState([]);
  const [iadeBelgeKey, setIadeBelgeKey] = useState('');     // "FATURA:<id>" | "GRN:<id>"
  const [iadeMiktar, setIadeMiktar] = useState({});         // { [kaynak_kalem_id]: "miktar string" }
  const [iadeLokasyon, setIadeLokasyon] = useState({});     // { [kaynak_kalem_id]: lokasyon_id } (kalem.lokasyon_id yoksa kullanıcı seçer)
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [iadeSaving, setIadeSaving] = useState(false);
  const [iadeLoading, setIadeLoading] = useState(false);
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useUrlState('arama', '', { debounceMs: 300 });
  const [firmaFilter, setFirmaFilter] = useUrlState('firma', 'TUMU'); // TUMU | INGA_BURGER | ALTI_USTU_KOFTE | TORUNA_KASAP
  const [firmalar, setFirmalar] = useState([]);
  // Firma seçiliyken AcikBorc ledger aggregat'ları (suppliers/balances?firma=...):
  // { firma, items: {tedarikci_id: {guncel_bakiye, fatura_toplam, gider_toplam, odenen_toplam}}, belirsiz, error }
  const [firmaBalances, setFirmaBalances] = useState(null);
  const [cariBalances, setCariBalances] = useState([]); // TUMU görünümü: AcikBorc ledger aggregat'ları (KPI kartları tek kaynak)
  const [kartlar, setKartlar] = useState(KREDI_KARTI_OPTIONS_FALLBACK);
  const [odemeYontemleri, setOdemeYontemleri] = useState(ODEME_YONTEMLERI_FALLBACK);
  const [refreshKey, setRefreshKey] = useState(0);
  const token = localStorage.getItem('access_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  // Seçili ödeme yöntemi + bayrakları (kart/çek alanları + İade modu buradan beslenir).
  // Effect/render'dan ÖNCE türetilir (İade effect dep'i kullanır — TDZ olmasın).
  const seciliYontem = odemeYontemleri.find(y => y.value === odemeForm.yontem) || {};
  const isIade = seciliYontem.stok_etkisi === 'IADE';

  const loadData = useCallback(async () => {
    try {
      const [tedRes, fatRes, odeRes, gbRes, kartRes, oyRes, firmaRes, lokRes] = await Promise.all([
        fetch(`${API}/api/tedarikciler`, { headers }),
        fetch(`${API}/api/faturalar`, { headers }),
        fetch(`${API}/api/odemeler`, { headers }),
        // Cari "Giderler" kolonu GiderBorc'tan (açık hesap, limitsiz) beslenir —
        // eski /finance/giderler?limit=200 ham listesi (nakit dahil + son 200 kesik)
        // tedarikçileri eksik gösteriyordu (18 tedarikçide sapma). Bakiye zaten
        // GiderBorc'tan hesaplanıyordu; kolon da artık aynı kaynak → tutarlı.
        fetch(`${API}/api/finance/gider-borclar`, { headers }),
        fetch(`${API}/api/kredi-kartlari?include_inactive=true`, { headers }),
        fetch(`${API}/api/odeme-yontemleri`, { headers }),
        fetch(`${API}/api/firmalar`, { headers }),
        // Lokasyonlar: İade akışında kalem.lokasyon_id yoksa kullanıcı seçer.
        // İkincil/fallback'lı (kart gibi) — banner tetiklemez.
        fetch(`${API}/api/lokasyonlar`, { headers }),
      ]);

      // Kritik fetch hatalarını yutma (#259): hangi kaynak çöktü, banner'da görünsün.
      // Kart/firma listesi ikincil (fallback'lı dropdown) — banner tetiklemez.
      const failed = [];
      let tedData = [];
      let fatData = [];
      let odeData = [];
      let gbData = [];
      if (tedRes.ok) tedData = await tedRes.json(); else failed.push(`Tedarikçiler (${tedRes.status})`);
      if (fatRes.ok) fatData = await fatRes.json(); else failed.push(`Faturalar (${fatRes.status})`);
      if (odeRes.ok) odeData = await odeRes.json(); else failed.push(`Ödemeler (${odeRes.status})`);
      if (gbRes.ok) {
        const gb = await gbRes.json();
        gbData = Array.isArray(gb) ? gb : [];
      } else {
        failed.push(`Gider borçları (${gbRes.status})`);
      }

      setFaturalar(Array.isArray(fatData) ? fatData : []);
      setOdemeler(Array.isArray(odeData) ? odeData : []);
      setGiderBorclar(gbData);

      // Kartlar: API'den {kod, son_dort_hane, aktif} → {value, label, aktif}
      if (kartRes.ok) {
        const kartData = await kartRes.json();
        if (Array.isArray(kartData) && kartData.length > 0) {
          setKartlar(kartData.map(k => ({ value: k.kod, label: k.son_dort_hane, aktif: k.aktif !== false })));
        }
      }

      // Ödeme yöntemleri: API'den {kod, ad, cek_ister, kart_ister, stok_etkisi} → dropdown şekli.
      // İkincil/fallback'lı (kart gibi) — banner tetiklemez, API çökerse seed fallback'ı kalır.
      if (oyRes.ok) {
        const oyData = await oyRes.json();
        if (Array.isArray(oyData) && oyData.length > 0) {
          setOdemeYontemleri(oyData.map(y => ({
            value: y.kod, label: y.ad,
            cek_ister: y.cek_ister, kart_ister: y.kart_ister, stok_etkisi: y.stok_etkisi,
          })));
        }
      }

      // Firmalar: dropdown DB'den beslenir (Anayasa M.1) — {kod, ad, tip, aktif}
      if (firmaRes.ok) {
        const firmaData = await firmaRes.json();
        setFirmalar(Array.isArray(firmaData) ? firmaData : []);
      }

      // Lokasyonlar: İade kalem lokasyon seçimi için. İkincil/fallback'lı — banner tetiklemez.
      if (lokRes.ok) {
        const lokData = await lokRes.json();
        setLokasyonlar(Array.isArray(lokData) ? lokData : []);
      }

      // Tüm bakiyeleri tek istekte çek (N+1 → 1); acik-borclar lazy (row expand'da)
      const balRes = await fetch(`${API}/api/finance/suppliers/balances`, { headers });
      if (!balRes.ok) failed.push(`Bakiyeler (${balRes.status})`);
      const balData = balRes.ok ? await balRes.json() : [];
      setCariBalances(Array.isArray(balData) ? balData : []);
      const balMap = Object.fromEntries(balData.map(b => [b.tedarikci_id, b.guncel_bakiye]));
      const enrichedTedarikciler = tedData
        .filter(t => t.aktif !== false)
        .map(t => ({ ...t, bakiye: balMap[t.id] || 0 }));
      setTedarikciler(enrichedTedarikciler);
      setEkstreByTedarikci({});
      setRefreshKey(k => k + 1);
      setError(failed.length > 0 ? failed.join(', ') : null);
    } catch (err) {
      logError('Cari hesap yükleme hatası:', err);
      setError('Bağlantı/sunucu hatası');
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // URL'deki ?tedarikci_id=X varsa ilgili tedarikçiyi otomatik seç
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tid = params.get('tedarikci_id');
    if (tid && !selectedTedarikci) {
      const idNum = parseInt(tid, 10);
      if (!Number.isNaN(idNum)) {
        setSelectedTedarikci(idNum);
        setActiveTab('hareketler');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cari ekstre lazy: tedarikçi seçilince yükle. TEK KAYNAK — hareketler +
  // her satırın yürüyen bakiyesi + net_bakiye backend'de tek hesaptan gelir.
  // refreshKey değişince (loadData/ödeme/silme sonrası) yeniden yükler.
  useEffect(() => {
    if (!selectedTedarikci) return;
    setEkstreError(null);
    const token = localStorage.getItem('access_token');
    const hdrs = { 'Authorization': `Bearer ${token}` };
    fetch(`${API}/api/finance/suppliers/${selectedTedarikci}/ekstre`, { headers: hdrs })
      .then(async r => {
        if (!r.ok) {
          // Sessiz [] dönme tuzağı (#259): 500'de hareketler "boş" görünüyordu —
          // kullanıcı "işlemlerim kaybolmuş" sanıyordu, oysa endpoint çöküyor.
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || `Sunucu hatası (${r.status})`);
        }
        return r.json();
      })
      .then(data => {
        setEkstreByTedarikci(prev => ({
          ...prev,
          [selectedTedarikci]: {
            net_bakiye: typeof data?.net_bakiye === 'number' ? data.net_bakiye : 0,
            hareketler: Array.isArray(data?.hareketler) ? data.hareketler : [],
          },
        }));
      })
      .catch(err => {
        logError('Ekstre yükleme hatası:', err);
        setEkstreError(err.message || 'Bağlantı/sunucu hatası');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTedarikci, refreshKey]);

  // Firma seçiliyken bakiye/Ödenen ledger'dan gelir. Odeme.firma artık ödemeyi
  // yapan (cash-out) firmayı taşır (ödeme formundan), ama buradaki firma görünümü
  // borç-tarafı (fatura/gider) bazlı — cash-out firmasıyla karıştırılmamalı. FIFO
  // firma sınırı tanımaz; backend, AcikBorc.odenen_tutar (FIFO'nun borçlara dağıttığı
  // pay) üzerinden hesaplar. refreshKey: ödeme/silme sonrası loadData ile yeniden çekilir.
  useEffect(() => {
    if (firmaFilter === 'TUMU') { setFirmaBalances(null); return; }
    const tkn = localStorage.getItem('access_token');
    let cancelled = false;
    fetch(`${API}/api/finance/suppliers/balances?firma=${encodeURIComponent(firmaFilter)}`, {
      headers: { 'Authorization': `Bearer ${tkn}` },
    })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(data => {
        if (cancelled) return;
        const items = Object.fromEntries((data.items || []).map(r => [r.tedarikci_id, r]));
        setFirmaBalances({ firma: data.firma, items, belirsiz: data.firmasi_belirsiz, error: false });
      })
      .catch(() => {
        if (!cancelled) setFirmaBalances({ firma: firmaFilter, items: {}, belirsiz: null, error: true });
      });
    return () => { cancelled = true; };
  }, [firmaFilter, refreshKey]);

  // İade modu açıkken (Ödeme Yap tab'ında İade yöntemi seçiliyse) iade edilebilir
  // belgeleri çek. Tedarikçi/mod değişince seçimleri sıfırla. refreshKey ile
  // loadData sonrası tazelenir (iade kaydı sonrası kalan_iade_edilebilir azalır).
  useEffect(() => {
    if (!(activeTab === 'odeme' && isIade && selectedTedarikci)) {
      setIadeBelgeler([]);
      return;
    }
    setIadeBelgeKey('');
    setIadeMiktar({});
    setIadeLokasyon({});
    setIadeLoading(true);
    const tkn = localStorage.getItem('access_token');
    fetch(`${API}/api/iade/edilebilir-belgeler?tedarikci_id=${selectedTedarikci}`, { headers: { 'Authorization': `Bearer ${tkn}` } })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || `Sunucu hatası (${r.status})`);
        }
        return r.json();
      })
      .then(data => {
        setIadeBelgeler(Array.isArray(data?.belgeler) ? data.belgeler : []);
      })
      .catch(err => {
        logError('İade edilebilir belge yükleme hatası:', err);
        toast.error(err.message || 'İade edilebilir belgeler yüklenemedi');
        setIadeBelgeler([]);
      })
      .finally(() => setIadeLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isIade, selectedTedarikci, refreshKey]);

  // Fatura içeriği (kalem) lazy yükleyici — fatura satırı genişletilince çağrılır.
  // Cache (ref) ile aynı fatura tekrar açılınca yeniden çekmez; StrictMode'da çift
  // fetch ve state-updater içinde side-effect olmaz.
  const loadFaturaKalemler = useCallback((faturaId) => {
    if (faturaKalemlerRef.current[faturaId]) return;
    faturaKalemlerRef.current[faturaId] = { loading: true };
    setFaturaKalemler(prev => ({ ...prev, [faturaId]: { loading: true } }));
    const tkn = localStorage.getItem('access_token');
    fetch(`${API}/api/faturalar/${faturaId}/kalemler`, { headers: { 'Authorization': `Bearer ${tkn}` } })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(data => {
        const entry = { loading: false, data };
        faturaKalemlerRef.current[faturaId] = entry;
        setFaturaKalemler(prev => ({ ...prev, [faturaId]: entry }));
      })
      .catch(error => {
        const entry = { loading: false, error: String(error) };
        faturaKalemlerRef.current[faturaId] = entry;
        setFaturaKalemler(prev => ({ ...prev, [faturaId]: entry }));
      });
  }, []);

  // Firma filtresi: bu görünüm borç tarafı (fatura/gider) kayıt-seviyesinde firma taşır.
  // Odeme.firma artık ödemeyi yapan (cash-out) firmayı taşır (ödeme formundan,
  // Ödeme Takibi'nde filtrelenebilir) — ama bu borç-tarafı firma görünümüyle
  // karıştırılmamalı: FIFO firma sınırı tanımaz, tek ödeme birden çok firmanın borcunu
  // kapatabilir. Bu yüzden firma görünümünde Ödenen/Bakiye client-side ödeme filtresinden
  // DEĞİL, AcikBorc ledger'ının FIFO dağıtımından gelir (firmaBalances). Eski client-side
  // hesap tüm ödemeleri (cash-out firmasına bakmadan) dışlayıp bakiyeleri şişiriyordu.
  const firmaAktif = firmaFilter !== 'TUMU';
  const matchFirma = (kayit) => !firmaAktif || (kayit.firma || null) === firmaFilter;
  const faturalarFiltered = faturalar.filter(matchFirma); // satır-içi vade gösterimi için
  const fbItems = (firmaAktif && firmaBalances && firmaBalances.items) || {};
  const fbRows = Object.values(fbItems);

  const tedarikcilerView = !firmaAktif
    ? tedarikciler
    : tedarikciler.map(t => ({ ...t, bakiye: (fbItems[t.id] && fbItems[t.id].guncel_bakiye) || 0 }));

  // KPI toplamları HER İKİ görünümde de AcikBorc ledger aggregat'larından (TEK kaynak):
  // firma → firmaBalances.items, TUMU → cariBalances. Böylece kartlar per-tedarikçi
  // liste + firma görünümüyle birebir tutar. Ham faturalar/gider_borclar KULLANILMAZ —
  // eski hesap IPTAL faturaları borç sayıp Net'i şişiriyordu (ledger IPTAL'i yazmaz) ve
  // Toplam Borç'tan gideri dışlıyordu. TUMU'da Net = Σorjinal − Σodeme (gerçek net,
  // fazla ödeme negatif); firma görünümünde açık-kalan kalır. Invariant: Borç−Ödenen=Net.
  const { toplamOdenen, toplamBorc, netBakiye } = hesaplaCariKpi(firmaAktif ? fbRows : cariBalances);
  const borcluTedarikci = tedarikcilerView.filter(t => t.bakiye > 0).length;

  const getTedarikciDetay = (tedarikciId) => {
    // TEK KAYNAK: hareketler + her satırın yürüyen bakiyesi (bakiye_sonrasi) backend
    // /suppliers/{id}/ekstre'den gelir (en yeni üstte, kronolojik+aynı-gün tie-break
    // backend'de). Frontend YALNIZ etiket/detay (sunum) üretir, bakiye HESAPLAMAZ —
    // rozet (net_bakiye) ile kolon (bakiye_sonrasi) aynı hesaptan → ayrışma imkânsız.
    const ekstre = ekstreByTedarikci[tedarikciId];
    const hareketler = (ekstre && ekstre.hareketler) || [];

    const yontemAdi = (yontem) => ({
      NAKIT: 'Nakit', HAVALE: 'Havale', KREDI_KARTI: 'Kredi Kartı',
      CEK: 'Çek', SENET: 'Senet', ACIK_HESAP: 'Açık Hesap',
      IADE: 'İade', FIYAT_FARKI: 'Fiyat Farkı',
    }[yontem] || (yontem || '-'));
    const fmtPara = (n) => `${(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`;

    return hareketler.map((h) => {
      const ortak = {
        id: h.id,
        kaynakId: h.kaynak_id,
        tarih: h.tarih || '-',
        borc: h.borc || 0,
        alacak: h.alacak || 0,
        durum: h.durum,
        bakiye_sonrasi: typeof h.bakiye_sonrasi === 'number' ? h.bakiye_sonrasi : 0,
      };

      if (h.tip === 'ODEME') {
        const kartNo = (h.odeme_detay || '').replace('KART_', '');
        let yDetay = '';
        if (h.odeme_yontemi === 'KREDI_KARTI' && kartNo) yDetay = ` (${kartNo})`;
        else if (h.odeme_yontemi === 'HAVALE' && h.odeme_detay) yDetay = ` (${h.odeme_detay})`;
        else if (h.odeme_yontemi === 'CEK' && h.cek_no) yDetay = ` #${h.cek_no}${h.cek_vade_tarihi ? ` (Vade: ${h.cek_vade_tarihi})` : ''}`;
        else if (h.odeme_yontemi === 'SENET' && h.cek_no) yDetay = ` #${h.cek_no}${h.cek_vade_tarihi ? ` (Vade: ${h.cek_vade_tarihi})` : ''}`;
        const yontemLabel = yontemAdi(h.odeme_yontemi);
        const detay = {
          'Tutar': fmtPara(h.borc),
          'Ödeme Yöntemi': h.odeme_yontemi || '-',
          'İşlem Tarihi': h.tarih || '-',
        };
        if (h.odeme_yontemi === 'CEK') { detay['Çek No'] = h.cek_no || '-'; detay['Çek Vade Tarihi'] = h.cek_vade_tarihi || '-'; }
        if (h.odeme_yontemi === 'SENET') { detay['Senet No'] = h.cek_no || '-'; detay['Senet Vade Tarihi'] = h.cek_vade_tarihi || '-'; }
        if (h.odeme_detay) detay['Kart / Hesap Detayı'] = h.odeme_detay;
        detay['Açıklama'] = h.aciklama || '-';
        return {
          ...ortak, tip: 'ODEME',
          aciklama: `Ödeme - ${yontemLabel}${yDetay}${h.aciklama ? ` — ${h.aciklama}` : ''}`,
          detay, _odemeYontemLabel: `${yontemLabel}${yDetay}`,
        };
      }

      if (h.tip === 'GIDER_PESIN' || h.tip === 'GIDER_PESIN_ODEME') {
        // Peşin (kart/nakit/havale ödenmiş) alış: net-0 "alış + ödeme" çifti
        const kartNo = (h.odeme_detay || '').replace('KART_', '');
        const yDetay = h.odeme_yontemi === 'KREDI_KARTI' && kartNo ? ` (${kartNo})`
          : (h.odeme_yontemi === 'HAVALE' && h.odeme_detay ? ` (${h.odeme_detay})` : '');
        const ortakDetay = {
          'Kaynak': `Gider #${h.kaynak_id}`,
          'Kalem': h.kalem || '-',
          'Kategori': h.kategori || '-',
          'Tutar': fmtPara(h.alacak || h.borc),
          'Ödeme Yöntemi': `${yontemAdi(h.odeme_yontemi)}${yDetay}`,
          'Firma': h.firma || '-',
        };
        if (h.tip === 'GIDER_PESIN') {
          return {
            ...ortak, tip: 'GIDER_PESIN',
            aciklama: `Alış${h.kalem ? ' — ' + h.kalem : ''}${h.firma ? ' — ' + h.firma : ''}`,
            detay: ortakDetay,
          };
        }
        return {
          ...ortak, tip: 'GIDER_PESIN_ODEME',
          aciklama: `Ödeme - ${yontemAdi(h.odeme_yontemi)}${yDetay}`,
          detay: ortakDetay,
        };
      }

      // FATURA | GIDER_BORC (açık hesap alışı)
      const isFatura = h.tip === 'FATURA';
      return {
        ...ortak, tip: isFatura ? 'FATURA' : 'GIDER_BORC',
        aciklama: isFatura
          ? `Fatura ${h.belge_no || h.fatura_no || '#' + h.kaynak_id}${h.firma ? ' — ' + h.firma : ''}`
          : `Alış${h.kalem ? ' — ' + h.kalem : ''}${h.firma ? ' — ' + h.firma : ''}`,
        detay: {
          'Kaynak': isFatura
            ? `Fatura ${h.belge_no || h.fatura_no || '#' + h.kaynak_id}`
            : `Açık Hesap Alış #${h.kaynak_id}`,
          ...(isFatura ? {} : { 'Kalem': h.kalem || '-', 'Kategori': h.kategori || '-' }),
          'Orjinal Tutar': fmtPara(h.alacak),
          'Ödenen': fmtPara(h.odenen_tutar),
          'Kalan': fmtPara(h.kalan_tutar),
          'Vade': h.vade || '-',
          'Durum': h.durum,
          'Firma': h.firma || '-',
        },
      };
    });
  };

  const handleOdemeKaydet = async () => {
    // Güvenlik kapısı (defense-in-depth): İade düz Ödeme olarak POST edilemez —
    // stok hareketi gerekir, İade akışı handleIadeKaydet → POST /api/iade kullanır.
    const yontemObj = odemeYontemleri.find(y => y.value === odemeForm.yontem) || {};
    if (yontemObj.stok_etkisi === 'IADE') {
      toast.warning('İade için fatura/GRN seçim akışını kullanın');
      return;
    }
    if (!odemeForm.tutar || (parseTutarTR(odemeForm.tutar) || 0) <= 0) {
      toast.warning('Geçerli bir tutar giriniz');
      return;
    }
    setOdemeSaving(true);
    try {
      const res = await fetch(`${API}/api/odemeler`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tedarikci_id: selectedTedarikci,
          tutar: parseTutarTR(odemeForm.tutar),
          odeme_yontemi: odemeForm.yontem,
          aciklama: odemeForm.aciklama || null,
          odeme_tarihi: odemeForm.tarih || null,
          // Alanlar bayrak-sürümlü (DB'den): yeni çek/kart-tipi yöntem eklenince payload da otomatik taşır.
          // HAVALE'nin opsiyonel banka/dekont alanı için bayrak yok → kod-bazlı (formla tutarlı).
          cek_no: yontemObj.cek_ister ? (odemeForm.cek_no || null) : null,
          cek_vade_tarihi: yontemObj.cek_ister ? (odemeForm.cek_vade_tarihi || null) : null,
          odeme_detay: (yontemObj.kart_ister || odemeForm.yontem === 'HAVALE') ? (odemeForm.odeme_detay || null) : null,
          // Ödemeyi yapan (cash-out) firma — opsiyonel. Ödeme Takibi'nde firma bazlı filtrelenir.
          firma: odemeForm.firma || null,
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ödeme kaydedilemedi');
      }
      toast.success(`${formatTutarTR(parseTutarTR(odemeForm.tutar))} ₺ ödeme kaydedildi`);
      setOdemeForm({ tutar: '', yontem: 'HAVALE', aciklama: '', tarih: new Date().toISOString().split('T')[0], cek_no: '', cek_vade_tarihi: '', odeme_detay: '', firma: '' });
      invalidateQueries(['cari', 'finance', 'dashboard', 'tedarikci', 'odeme']);
      await loadData();
      setActiveTab('hareketler');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOdemeSaving(false);
    }
  };

  const handleIadeKaydet = async () => {
    const seciliBelge = iadeBelgeler.find(b => `${b.tip}:${b.belge_id}` === iadeBelgeKey);
    if (!seciliBelge) { toast.warning('Önce bir belge seçin'); return; }
    const kalemler = [];
    for (const k of seciliBelge.kalemler) {
      const miktar = parseDecimal(iadeMiktar[k.kaynak_kalem_id], 0);
      if (!miktar || miktar <= 0) continue;
      if (miktar > k.kalan_iade_edilebilir + 1e-9) {
        toast.error(`${k.malzeme_adi}: en fazla ${k.kalan_iade_edilebilir} iade edilebilir`); return;
      }
      const lokId = k.lokasyon_id || iadeLokasyon[k.kaynak_kalem_id];
      if (!lokId) { toast.error(`${k.malzeme_adi}: lokasyon seçin`); return; }
      kalemler.push({ kaynak_tip: k.kaynak_tip, kaynak_kalem_id: k.kaynak_kalem_id,
        malzeme_id: k.malzeme_id, miktar, birim_fiyat: k.birim_fiyat, lokasyon_id: lokId });
    }
    if (!kalemler.length) { toast.warning('En az bir kalem için iade miktarı girin'); return; }
    setIadeSaving(true);
    try {
      const res = await fetch(`${API}/api/iade`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tedarikci_id: selectedTedarikci, tarih: odemeForm.tarih || null,
          aciklama: odemeForm.aciklama || null, kalemler }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'İade kaydedilemedi'); }
      const data = await res.json();
      toast.success(`İade kaydedildi (${(data.tutar || 0).toLocaleString('tr-TR')} ₺)`);
      setIadeBelgeKey(''); setIadeMiktar({}); setIadeLokasyon({});
      invalidateQueries(['cari', 'finance', 'dashboard', 'tedarikci', 'odeme', 'stok']);
      await loadData();
      setActiveTab('hareketler');
    } catch (err) { toast.error(err.message); } finally { setIadeSaving(false); }
  };

  const handleFaturaIptal = async (faturaId) => {
    if (!window.confirm('Bu faturayı iptal etmek istediğinize emin misiniz? Stok ve fiyat kayıtları da etkilenecektir.')) return;
    try {
      const res = await fetch(`${API}/api/faturalar/${faturaId}`, { method: 'DELETE', headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Silme başarısız');
      }
      toast.success('Fatura iptal edildi');
      invalidateQueries(['cari', 'finance', 'dashboard', 'tedarikci', 'fatura']);
      await loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleOdemeSil = async (odemeId, tutar, yontemLabel) => {
    const tutarStr = (tutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
    const isIadeSilme = (yontemLabel || '').includes('İade');
    if (!window.confirm(
      `Bu ${isIadeSilme ? 'iadeyi' : 'ödemeyi'} silmek istediğinize emin misiniz?\n\n` +
      `Tutar: ${tutarStr} ₺\n` +
      `Yöntem: ${yontemLabel}\n\n` +
      (isIadeSilme
        ? `İade geri alınacak: stok geri eklenecek ve cari hesap bakiyesi düzeltilecek.`
        : `Cari hesap bakiyesi otomatik düzeltilecek.`) +
      ` Bu işlem geri alınamaz.`
    )) return;
    try {
      const res = await fetch(`${API}/api/odemeler/${odemeId}`, { method: 'DELETE', headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Silme başarısız');
      }
      toast.success(isIadeSilme ? `İade geri alındı (${tutarStr} ₺)` : `Ödeme silindi (${tutarStr} ₺)`);
      // İade silmesi stoğu da geri eklediği için stok cache'ini de tazele.
      invalidateQueries(['cari', 'finance', 'dashboard', 'tedarikci', 'odeme', ...(isIadeSilme ? ['stok'] : [])]);
      await loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getBakiyeStyle = (bakiye) => {
    if (bakiye > 0) return { color: '#d32f2f', bg: '#ffebee', text: 'Alacaklı' };
    if (bakiye < 0) return { color: '#2e7d32', bg: '#e8f5e9', text: 'Borçlu' };
    return { color: colors.textSecondary, bg: '#f5f5f5', text: 'Dengede' };
  };

  const handleTumBakiyeleriYenile = async () => {
    if (!window.confirm(
      `Tüm aktif tedarikçilerin cari hesap bakiyeleri taranacak ve drift varsa otomatik düzeltilecek.\n\n` +
      `Bu işlem güvenlidir — sadece tutarsızlık varsa replay yapılır. Birkaç saniye sürebilir.`
    )) return;
    try {
      const res = await fetch(`${API}/api/admin/reapply-all-suppliers-fifo?only_with_drift=true`, {
        method: 'POST', headers
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'İşlem başarısız');
      }
      const data = await res.json();
      const totalDiff = (data.total_diff || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
      toast.success(`${data.replayed_count || 0} tedarikçi düzeltildi (${data.skipped || 0} atlandı, toplam fark: ${totalDiff} ₺)`);
      invalidateQueries(['cari', 'finance', 'dashboard', 'tedarikci']);
      await loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleBakiyeYenile = async (tedarikciId, unvan) => {
    if (!window.confirm(
      `${unvan} için cari hesap bakiyesi yeniden hesaplanacak.\n\n` +
      `Tüm faturalar + ödemeler FIFO sırasıyla yeniden uygulanır. ` +
      `Bu işlem güvenli — sadece hesaplamayı düzeltir.`
    )) return;
    try {
      const res = await fetch(`${API}/api/admin/reapply-supplier-fifo/${tedarikciId}`, {
        method: 'POST', headers
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'İşlem başarısız');
      }
      await res.json().catch(() => ({}));
      // Reapply endpoint'i 'açık kalan' (Σkalan) döner; rozet/ekstre ise gerçek net
      // gösterir. Çelişkili sayı basmamak için tutarı toast'ta GÖSTERME — loadData
      // hemen ardından doğru net'i tazeler.
      toast.success('Bakiye yenilendi (FIFO yeniden uygulandı)');
      invalidateQueries(['cari', 'finance', 'dashboard', 'tedarikci']);
      await loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const s = {
    container: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '10px' },
    title: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    kpiCard: (color) => ({
      background: colors.surface, borderRadius: radius.lg, padding: '20px',
      borderLeft: `4px solid ${color}`, boxShadow: shadows.md
    }),
    kpiLabel: { fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' },
    kpiValue: (color) => ({ fontSize: '28px', fontWeight: '700', color }),
    kpiSub: { fontSize: '12px', color: colors.textTertiary, marginTop: '4px' },
    card: { background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.md, overflow: 'hidden', marginBottom: '20px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '12px 16px', textAlign: 'left', background: colors.backgroundAlt, color: colors.textSecondary, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: `2px solid ${colors.border}` },
    td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '14px' },
    badge: (bg, color) => ({ display: 'inline-block', padding: '4px 10px', borderRadius: radius.xl, fontSize: '12px', fontWeight: '500', background: bg, color }),
    clickableRow: { cursor: 'pointer', transition: 'background 0.15s' },
    backBtn: {
      padding: '8px 16px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surface,
      cursor: 'pointer', fontSize: '13px', color: '#555'
    },
    tab: (active) => ({
      padding: '10px 24px', border: 'none', borderBottom: active ? '3px solid #1976d2' : '3px solid transparent',
      background: 'transparent', cursor: 'pointer', fontSize: '14px', fontWeight: active ? '600' : '400',
      color: active ? '#1976d2' : '#666', transition: 'all 0.2s'
    }),
    input: { width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' },
    select: { width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box', background: colors.surface },
    formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' },
    label: { fontSize: '12px', color: colors.textSecondary, fontWeight: '600', marginBottom: '6px', display: 'block' },
    saveBtn: (disabled) => ({
      padding: '12px 32px', background: disabled ? '#ccc' : '#1976d2', color: '#fff', border: 'none',
      borderRadius: radius.md, fontSize: '15px', fontWeight: '600', cursor: disabled ? 'default' : 'pointer'
    }),
    expandedDetail: {
      background: colors.backgroundAlt, padding: '12px 20px 12px 48px', borderBottom: `1px solid ${colors.border}`,
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', fontSize: '13px'
    },
    detailItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
    detailLabel: { fontSize: '11px', color: colors.textTertiary, fontWeight: '600', textTransform: 'uppercase' },
    detailValue: { fontSize: '13px', color: '#333' },
    msg: (type) => ({
      padding: '12px 16px', borderRadius: radius.md, marginBottom: '16px', fontSize: '14px',
      background: type === 'success' ? '#e8f5e9' : '#ffebee',
      color: type === 'success' ? '#2e7d32' : '#d32f2f',
      border: `1px solid ${type === 'success' ? '#a5d6a7' : '#ef9a9a'}`
    })
  };

  if (loading && tedarikciler.length === 0) return <div style={s.container}><p>Yükleniyor...</p></div>;

  const selectedTed = tedarikcilerView.find(t => t.id === selectedTedarikci);

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.title}>Cari Hesaplar</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <select
            value={firmaFilter}
            onChange={e => setFirmaFilter(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', background: colors.surface, cursor: 'pointer' }}
          >
            <option value="TUMU">Tüm Firmalar</option>
            {firmalar.filter(f => f.aktif !== false).map(f => (
              <option key={f.kod} value={f.kod}>{f.ad}{f.tip === 'MASRAF_MERKEZI' ? ' (Masraf Merkezi)' : ''}</option>
            ))}
          </select>
          <span style={{ fontSize: '13px', color: colors.textTertiary }}>{tedarikcilerView.length} tedarikçi · Tüm tutarlar KDV dahil</span>
          <button
            onClick={handleTumBakiyeleriYenile}
            title="Tüm tedarikçileri tarar, drift varsa otomatik düzeltir"
            style={{
              padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
              background: '#fff', color: colors.textSecondary, fontSize: '13px', cursor: 'pointer'
            }}
          >🔄 Tüm Bakiyeleri Yenile</button>
        </div>
      </div>

      <ErrorBanner error={error} onRetry={loadData} />

      {/* Firma görünümü uyarıları */}
      {firmaAktif && firmaBalances && firmaBalances.error && (
        <ErrorBanner error="firma bazlı bakiyeler" onRetry={() => setRefreshKey(k => k + 1)} />
      )}
      {firmaAktif && firmaBalances && firmaBalances.belirsiz && firmaBalances.belirsiz.adet > 0 && (
        <div style={{
          fontSize: '13px', color: '#8a6d3b', background: '#fcf8e3',
          border: '1px solid #faebcc', borderRadius: radius.sm,
          padding: '8px 12px', marginBottom: '12px',
        }}>
          ℹ️ {firmaBalances.belirsiz.adet} borç kaydının firması belirsiz
          (kalan {(firmaBalances.belirsiz.kalan_toplam || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺)
          — bu görünümdeki toplamlara dahil değil; "Tüm Firmalar" görünümünde yer alır.
        </div>
      )}

      {/* KPI Cards */}
      <div style={s.kpiRow}>
        <div style={s.kpiCard('#d32f2f')}>
          <div style={s.kpiLabel}>Toplam Borç</div>
          <div style={s.kpiValue('#d32f2f')}>{toplamBorc.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
          <div style={s.kpiSub}>{borcluTedarikci} tedarikçiye</div>
        </div>
        <div style={s.kpiCard('#2e7d32')}>
          <div style={s.kpiLabel}>Toplam Ödenen</div>
          <div style={s.kpiValue('#2e7d32')}>{toplamOdenen.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
          {firmaAktif && <div style={s.kpiSub}>FIFO dağıtımına göre</div>}
        </div>
        <div style={s.kpiCard('#1976d2')}>
          <div style={s.kpiLabel}>Net Bakiye</div>
          <div style={s.kpiValue('#1976d2')}>{netBakiye.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
        </div>
        <div style={s.kpiCard('#7b1fa2')}>
          <div style={s.kpiLabel}>Aktif Tedarikçi</div>
          <div style={s.kpiValue('#7b1fa2')}>{tedarikcilerView.length}</div>
          <div style={s.kpiSub}>{borcluTedarikci} borçlu</div>
        </div>
      </div>

      {/* Detail View */}
      {selectedTedarikci && selectedTed && (
        <div style={s.card}>
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <button style={s.backBtn} onClick={() => { setSelectedTedarikci(null); setActiveTab('hareketler'); setExpandedRow(null); }}>
                ← Listeye Dön
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>{selectedTed.unvan}</span>
                {selectedTed.muhasebe_kodu && (
                  <span style={{ fontSize: '12px', color: colors.textSecondary, fontFamily: 'monospace', background: '#e3f2fd', padding: '2px 8px', borderRadius: radius.sm }}>
                    {selectedTed.muhasebe_kodu}
                  </span>
                )}
                {(() => {
                  // Rozet = ekstre net_bakiye (kolonun son satırıyla AYNI hesap, tek
                  // kaynak). Ekstre henüz yüklenmediyse liste bakiyesine (batch, aynı
                  // tanım B) düş.
                  const ne = ekstreByTedarikci[selectedTedarikci]?.net_bakiye;
                  const b = typeof ne === 'number' ? ne : selectedTed.bakiye;
                  const st = getBakiyeStyle(b);
                  return (
                    <span style={s.badge(st.bg, st.color)}>
                      {Math.abs(b).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺ {st.text}
                    </span>
                  );
                })()}
                <button
                  onClick={() => handleBakiyeYenile(selectedTed.id, selectedTed.unvan)}
                  title="Fatura + ödemeler FIFO sırasıyla yeniden uygulanır"
                  style={{
                    background: '#fff', border: `1px solid ${colors.border}`, color: colors.textSecondary,
                    padding: '4px 10px', borderRadius: radius.sm, fontSize: '12px', cursor: 'pointer'
                  }}
                >🔄 Bakiyeyi Yenile</button>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ borderBottom: `2px solid ${colors.border}`, marginBottom: '16px', display: 'flex' }}>
              <button style={s.tab(activeTab === 'hareketler')} onClick={() => setActiveTab('hareketler')}>
                Hesap Hareketleri
              </button>
              <button style={s.tab(activeTab === 'odeme')} onClick={() => setActiveTab('odeme')}>
                Ödeme Yap
              </button>
            </div>

            {/* Tab: Hareketler */}
            {activeTab === 'hareketler' && (
              <div style={{ overflowX: 'auto' }}>
                {/* Retry: refreshKey bump'ı yalnız acik-borclar effect'ini yeniden çalıştırır */}
                <ErrorBanner error={ekstreError} onRetry={() => setRefreshKey(k => k + 1)} />
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width: '30px' }}></th>
                      <th style={s.th}>Tarih</th>
                      <th style={s.th}>Tip</th>
                      <th style={s.th}>Açıklama</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Borç</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Alacak</th>
                      <th style={{ ...s.th, textAlign: 'right' }} title="Bu işlem sonrası cari bakiye">Bakiye (Sonrası)</th>
                      <th style={s.th}>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Hareketler + her satırın bakiye_sonrası backend ekstresinden
                      // (en yeni üstte, kronolojik+aynı-gün tie-break backend'de işlendi).
                      const hareketler = getTedarikciDetay(selectedTedarikci);
                      if (hareketler.length === 0) {
                        // Hata varken "Hareket bulunamadı" yanıltıcı — ekstre yüklenememiş olabilir.
                        if (ekstreError) {
                          return <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: '#a4342b', padding: '40px' }}>Hareketler yüklenemedi — yukarıdan tekrar deneyin.</td></tr>;
                        }
                        return <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: colors.textTertiary, padding: '40px' }}>Hareket bulunamadı</td></tr>;
                      }

                      return hareketler.map((h) => {
                        const isExpanded = expandedRow === h.id;
                        const bk = h.bakiye_sonrasi || 0;
                        const tipColors = {
                          FATURA: { bg: '#fff3e0', color: '#e65100', label: 'Fatura' },
                          // Alış = tedarikçiden alım. Açık hesap (GIDER_BORC) açık kalır
                          // ve bakiyeye yansır; peşin (GIDER_PESIN) ödenmiş alıştır.
                          // 'Açık Hesap' bir ödeme yöntemi olduğundan satır etiketi değil.
                          GIDER_BORC: { bg: '#ede7f6', color: '#5e35b1', label: 'Alış' },
                          GIDER_PESIN: { bg: '#ede7f6', color: '#5e35b1', label: 'Alış' },
                          ODEME: { bg: '#e8f5e9', color: '#2e7d32', label: 'Ödeme' },
                          GIDER_PESIN_ODEME: { bg: '#e8f5e9', color: '#2e7d32', label: 'Ödeme' },
                        };
                        const tc = tipColors[h.tip] || tipColors.FATURA;
                        return (
                          <React.Fragment key={h.id}>
                            <tr
                              style={{ ...s.clickableRow, background: isExpanded ? '#f0f4ff' : '#fff' }}
                              onClick={() => {
                                const willExpand = !isExpanded;
                                setExpandedRow(willExpand ? h.id : null);
                                if (willExpand && h.tip === 'FATURA' && h.kaynakId) loadFaturaKalemler(h.kaynakId);
                              }}
                              onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fafafa'; }}
                              onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fff'; }}
                            >
                              <td style={{ ...s.td, fontSize: '16px', color: colors.textTertiary, textAlign: 'center' }}>
                                {isExpanded ? '▼' : '▶'}
                              </td>
                              <td style={s.td}>{fmtTarih(h.tarih)}</td>
                              <td style={s.td}>
                                <span style={s.badge(tc.bg, tc.color)}>{tc.label}</span>
                              </td>
                              <td style={{ ...s.td, maxWidth: '300px' }}>{h.aciklama}</td>
                              <td style={{ ...s.td, textAlign: 'right', color: h.borc > 0 ? '#d32f2f' : '#ccc', fontWeight: h.borc > 0 ? '600' : '400' }}>
                                {h.borc > 0 ? `${h.borc.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}
                              </td>
                              <td style={{ ...s.td, textAlign: 'right', color: h.alacak > 0 ? '#2e7d32' : '#ccc', fontWeight: h.alacak > 0 ? '600' : '400' }}>
                                {h.alacak > 0 ? `${h.alacak.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}
                              </td>
                              <td style={{
                                ...s.td,
                                textAlign: 'right',
                                fontWeight: '700',
                                fontFamily: 'monospace',
                                color: bk > 0 ? '#d32f2f' : bk < 0 ? '#2e7d32' : colors.textSecondary,
                              }}>
                                {bk.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                              </td>
                              <td style={s.td}>
                                <span style={s.badge(
                                  h.durum === 'ODENDI' ? '#e8f5e9' : h.durum === 'ODENECEK' ? '#fff3e0' : '#e3f2fd',
                                  h.durum === 'ODENDI' ? '#2e7d32' : h.durum === 'ODENECEK' ? '#e65100' : '#1565c0'
                                )}>{h.durum}</span>
                              </td>
                            </tr>
                            {isExpanded && h.detay && (
                              <tr>
                                <td colSpan={8} style={{ padding: 0 }}>
                                  <div style={s.expandedDetail}>
                                    {Object.entries(h.detay).map(([key, val]) => (
                                      <div key={key} style={s.detailItem}>
                                        <span style={s.detailLabel}>{key}</span>
                                        <span style={s.detailValue}>{val}</span>
                                      </div>
                                    ))}
                                    {h.tip === 'FATURA' && h.kaynakId && (() => {
                                      const fk = faturaKalemler[h.kaynakId];
                                      const box = { gridColumn: '1 / -1', marginTop: '4px', padding: '10px 12px', background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radius.sm };
                                      if (!fk || fk.loading) {
                                        return <div style={{ ...box, color: colors.textTertiary, fontSize: '13px' }}>Kalemler yükleniyor…</div>;
                                      }
                                      if (fk.error) {
                                        return <div style={{ ...box, color: '#b91c1c', fontSize: '13px' }}>Kalemler yüklenemedi.</div>;
                                      }
                                      const data = fk.data || {};
                                      const kalemler = data.kalemler || [];
                                      if (kalemler.length === 0) {
                                        return <div style={{ ...box, color: colors.textTertiary, fontSize: '13px' }}>Bu fatura için kalem detayı bulunamadı (Logo'dan veya elle girilmiş olabilir).</div>;
                                      }
                                      const ozet = data.ozet || {};
                                      const fark = Math.abs((ozet.kalem_net_toplam || 0) - (ozet.fatura_net || 0));
                                      const fmt = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
                                      return (
                                        <div style={box}>
                                          <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '6px' }}>Fatura İçeriği</div>
                                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <thead>
                                              <tr style={{ color: colors.textTertiary, textAlign: 'left' }}>
                                                <th style={{ padding: '4px 6px' }}>Malzeme</th>
                                                <th style={{ padding: '4px 6px', textAlign: 'right' }}>Miktar</th>
                                                <th style={{ padding: '4px 6px', textAlign: 'right' }}>Birim Fiyat</th>
                                                <th style={{ padding: '4px 6px', textAlign: 'right' }}>KDV</th>
                                                <th style={{ padding: '4px 6px', textAlign: 'right' }}>Tutar</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {kalemler.map((k, i) => (
                                                <tr key={i} style={{ borderTop: `1px solid ${colors.border}` }}>
                                                  <td style={{ padding: '4px 6px' }}>{k.malzeme_adi}</td>
                                                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(k.miktar)}{k.birim ? ` ${k.birim}` : ''}</td>
                                                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(k.birim_fiyat)} ₺</td>
                                                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{k.kdv_orani != null ? `%${k.kdv_orani}` : '-'}</td>
                                                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{fmt(k.satir_tutar)} ₺</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                          <div style={{ marginTop: '6px', fontSize: '12px', color: colors.textSecondary }}>
                                            Kalem toplamı (net): <strong>{fmt(ozet.kalem_net_toplam)} ₺</strong> · Fatura net: <strong>{fmt(ozet.fatura_net)} ₺</strong>
                                            {fark > 1 && <span style={{ color: '#b45309' }}> · ⚠ Fark: {fmt(fark)} ₺</span>}
                                          </div>
                                          {data.eslesme === 'tarih_bazli' && (
                                            <div style={{ marginTop: '4px', fontSize: '11px', color: colors.textTertiary }}>
                                              Fiş no olmadığı için tedarikçi+tarih eşleşmesi — aynı gün başka fiş varsa fazladan kalem görünebilir.
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    {h.tip === 'FATURA' && h.durum !== 'IPTAL' && h.kaynakId && (
                                      <div style={s.detailItem}>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleFaturaIptal(h.kaynakId); }}
                                          style={{
                                            padding: '6px 14px', background: '#fee2e2', color: '#991b1b',
                                            border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer',
                                            fontSize: '12px', fontWeight: 600, marginTop: '8px',
                                          }}
                                        >
                                          Faturayı İptal Et
                                        </button>
                                      </div>
                                    )}
                                    {h.tip === 'ODEME' && (
                                      <div style={s.detailItem}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // h.kaynakId = Odeme.id (backend kaynak_id).
                                            // NOT h.id (artık 'od-{id}' formatında).
                                            handleOdemeSil(
                                              h.kaynakId,
                                              h.borc,
                                              h._odemeYontemLabel || 'Ödeme',
                                            );
                                          }}
                                          style={{
                                            padding: '6px 14px', background: '#fee2e2', color: '#991b1b',
                                            border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer',
                                            fontSize: '12px', fontWeight: 600, marginTop: '8px',
                                          }}
                                        >
                                          Ödemeyi Sil
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tab: Ödeme Yap */}
            {activeTab === 'odeme' && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ background: '#e3f2fd', borderRadius: radius.md, padding: '12px 16px', marginBottom: '20px', fontSize: '14px', color: '#1565c0' }}>
                  <strong>{selectedTed.unvan}</strong> — Mevcut borç: <strong>{Math.abs(selectedTed.bakiye).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</strong>
                </div>
                {/* Ödeme yöntemi seçimi — İade modunda da görünür (kullanıcı geri dönebilsin) */}
                <div style={s.formRow}>
                  <div>
                    <label style={s.label}>Ödeme Yöntemi</label>
                    <select
                      value={odemeForm.yontem}
                      onChange={e => setOdemeForm(p => ({ ...p, yontem: e.target.value, odeme_detay: '', cek_no: '', cek_vade_tarihi: '' }))}
                      style={s.select}
                    >
                      {odemeYontemleri.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                    </select>
                  </div>
                </div>

                {isIade ? (
                  /* ---- İade modu: kaynak belge/kalem seçimi + miktar + lokasyon ---- */
                  (() => {
                    const seciliBelge = iadeBelgeler.find(b => `${b.tip}:${b.belge_id}` === iadeBelgeKey);
                    const lokAdById = (id) => {
                      const l = lokasyonlar.find(x => String(x.id) === String(id));
                      return l ? l.ad : `#${id}`;
                    };
                    const toplamIade = seciliBelge
                      ? seciliBelge.kalemler.reduce((sum, k) =>
                          sum + parseDecimal(iadeMiktar[k.kaynak_kalem_id], 0) * (k.birim_fiyat || 0) * (1 + (k.kdv_orani || 0) / 100), 0)
                      : 0;
                    return (
                      <>
                        <div style={{ background: '#fff8e1', borderRadius: radius.md, padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#8d6e00', border: '1px solid #ffe082' }}>
                          İade edilecek malları kaynak belgeden seçin. Stok düşer, cari azalır.
                        </div>
                        <div style={s.formRow}>
                          <div>
                            <label style={s.label}>Kaynak Belge *</label>
                            <select
                              value={iadeBelgeKey}
                              onChange={e => setIadeBelgeKey(e.target.value)}
                              style={s.select}
                              disabled={iadeLoading}
                            >
                              <option value="">{iadeLoading ? 'Yükleniyor…' : (iadeBelgeler.length === 0 ? 'Bu tedarikçi için iade edilebilir belge yok' : 'Belge seçin')}</option>
                              {iadeBelgeler.map(b => (
                                <option key={`${b.tip}:${b.belge_id}`} value={`${b.tip}:${b.belge_id}`}>
                                  {b.tip} {b.belge_no} — {b.tarih || ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {seciliBelge && (
                          <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                            <table style={s.table}>
                              <thead>
                                <tr>
                                  <th style={s.th}>Malzeme</th>
                                  <th style={{ ...s.th, textAlign: 'right' }}>Alınan</th>
                                  <th style={{ ...s.th, textAlign: 'right' }}>Kalan İade</th>
                                  <th style={{ ...s.th, textAlign: 'right' }}>Birim Fiyat</th>
                                  <th style={{ ...s.th, textAlign: 'right', width: '130px' }}>İade Miktarı</th>
                                  <th style={s.th}>Lokasyon</th>
                                </tr>
                              </thead>
                              <tbody>
                                {seciliBelge.kalemler.map(k => {
                                  const tukenmis = (k.kalan_iade_edilebilir || 0) <= 0;
                                  return (
                                    <tr key={k.kaynak_kalem_id} style={{ opacity: tukenmis ? 0.5 : 1 }}>
                                      <td style={s.td}>{k.malzeme_adi}</td>
                                      <td style={{ ...s.td, textAlign: 'right' }}>{(k.alinan_miktar || 0).toLocaleString('tr-TR')}{k.birim ? ` ${k.birim}` : ''}</td>
                                      <td style={{ ...s.td, textAlign: 'right', fontWeight: '600' }}>{(k.kalan_iade_edilebilir || 0).toLocaleString('tr-TR')}{k.birim ? ` ${k.birim}` : ''}</td>
                                      <td style={{ ...s.td, textAlign: 'right' }}>{(k.birim_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                                      <td style={{ ...s.td, textAlign: 'right' }}>
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          value={iadeMiktar[k.kaynak_kalem_id] ?? ''}
                                          onChange={e => setIadeMiktar(p => ({ ...p, [k.kaynak_kalem_id]: normalizeDecimalInput(e.target.value) }))}
                                          placeholder="0"
                                          disabled={tukenmis}
                                          style={{ ...s.input, textAlign: 'right', padding: '6px 8px' }}
                                        />
                                      </td>
                                      <td style={s.td}>
                                        {k.lokasyon_id ? (
                                          <span style={{ fontSize: '13px', color: colors.textSecondary }}>{lokAdById(k.lokasyon_id)}</span>
                                        ) : (
                                          <select
                                            value={iadeLokasyon[k.kaynak_kalem_id] ?? ''}
                                            onChange={e => setIadeLokasyon(p => ({ ...p, [k.kaynak_kalem_id]: e.target.value }))}
                                            disabled={tukenmis}
                                            style={{ ...s.select, padding: '6px 8px' }}
                                          >
                                            <option value="">Lokasyon seçin</option>
                                            {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
                                          </select>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            <div style={{ textAlign: 'right', marginTop: '12px', fontSize: '15px', fontWeight: '700', color: '#1a1a2e' }}>
                              Toplam İade Tutarı: {toplamIade.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                            </div>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                          <button style={s.backBtn} onClick={() => setActiveTab('hareketler')}>İptal</button>
                          <button style={s.saveBtn(iadeSaving)} onClick={handleIadeKaydet} disabled={iadeSaving}>
                            {iadeSaving ? 'Kaydediliyor...' : 'İadeyi Kaydet'}
                          </button>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  /* ---- Normal ödeme modu ---- */
                  <>
                    <div style={s.formRow}>
                      <div>
                        <label style={s.label}>Ödeme Tutarı (₺) *</label>
                        <input
                          type="text" inputMode="decimal"
                          value={odemeForm.tutar}
                          onChange={e => setOdemeForm(p => ({ ...p, tutar: e.target.value }))}
                          placeholder="0,00"
                          style={s.input}
                        />
                      </div>
                      <div>
                        <label style={s.label}>Ödemeyi Yapan Firma (opsiyonel)</label>
                        <select
                          value={odemeForm.firma}
                          onChange={e => setOdemeForm(p => ({ ...p, firma: e.target.value }))}
                          style={s.select}
                        >
                          <option value="">— Firma (ödemeyi yapan) —</option>
                          {firmalar.filter(f => f.aktif !== false).map(f => (
                            <option key={f.kod} value={f.kod}>{f.ad}{f.tip === 'MASRAF_MERKEZI' ? ' (Masraf Merkezi)' : ''}</option>
                          ))}
                        </select>
                      </div>
                      {seciliYontem.kart_ister && (
                        <div>
                          <label style={s.label}>Kart Secimi</label>
                          <select
                            value={odemeForm.odeme_detay}
                            onChange={e => setOdemeForm(p => ({ ...p, odeme_detay: e.target.value }))}
                            style={s.select}
                          >
                            <option value="">Kart Seciniz</option>
                            {kartlar.filter(k => k.aktif !== false).map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                          </select>
                        </div>
                      )}
                      {/* Banka/dekont alanı için DB bayrağı yok — HAVALE'ye özgü kalıyor (kod-bazlı bilinçli) */}
                      {odemeForm.yontem === 'HAVALE' && (
                        <div>
                          <label style={s.label}>Banka / Dekont No (opsiyonel)</label>
                          <input
                            value={odemeForm.odeme_detay}
                            onChange={e => setOdemeForm(p => ({ ...p, odeme_detay: e.target.value }))}
                            placeholder="Banka adi veya dekont no"
                            style={s.input}
                          />
                        </div>
                      )}
                      {/* Çek/Senet ortak: alan başlıkları DB'deki ad'dan ("Çek No" / "Senet No") */}
                      {seciliYontem.cek_ister && (
                        <>
                          <div>
                            <label style={s.label}>{seciliYontem.label} No</label>
                            <input
                              value={odemeForm.cek_no}
                              onChange={e => setOdemeForm(p => ({ ...p, cek_no: e.target.value }))}
                              placeholder={`${seciliYontem.label} numarasi`}
                              style={s.input}
                            />
                          </div>
                          <div>
                            <label style={s.label}>{seciliYontem.label} Vade Tarihi</label>
                            <input
                              type="date"
                              value={odemeForm.cek_vade_tarihi}
                              onChange={e => setOdemeForm(p => ({ ...p, cek_vade_tarihi: e.target.value }))}
                              style={s.input}
                            />
                          </div>
                        </>
                      )}
                      <div>
                        <label style={s.label}>Ödeme Tarihi *</label>
                        <input
                          type="date"
                          value={odemeForm.tarih}
                          onChange={e => setOdemeForm(p => ({ ...p, tarih: e.target.value }))}
                          style={s.input}
                        />
                      </div>
                      <div>
                        <label style={s.label}>Açıklama</label>
                        <input
                          value={odemeForm.aciklama}
                          onChange={e => setOdemeForm(p => ({ ...p, aciklama: e.target.value }))}
                          placeholder="Opsiyonel not..."
                          style={s.input}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                      <button style={s.backBtn} onClick={() => setActiveTab('hareketler')}>İptal</button>
                      <button style={s.saveBtn(odemeSaving)} onClick={handleOdemeKaydet} disabled={odemeSaving}>
                        {odemeSaving ? 'Kaydediliyor...' : 'Ödemeyi Kaydet'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Supplier List */}
      {!selectedTedarikci && (
        <div style={s.card}>
          <div style={{ padding: '16px 16px 0' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Tedarikçi ara..."
              style={{
                width: '100%', padding: '10px 16px', border: '2px solid #e0e0e0',
                borderRadius: radius.md, fontSize: '15px', boxSizing: 'border-box',
                outline: 'none', transition: 'border-color 0.2s'
              }}
              onFocus={e => e.target.style.borderColor = '#1976d2'}
              onBlur={e => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Tedarikçi</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Fatura (KDV Dahil)</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Giderler</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Ödenen</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Bakiye</th>
                  <th style={s.th}>Durum</th>
                  <th style={s.th}>Vade</th>
                </tr>
              </thead>
              <tbody>
                {tedarikcilerView.length === 0 ? (
                  <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: colors.textTertiary, padding: '40px' }}>Tedarikçi bulunamadı</td></tr>
                ) : tedarikcilerView
                  .filter(t => !searchQuery || searchMatch(t.unvan, searchQuery))
                  // Firma görünümünde yalnız o firmada borç kaydı (AcikBorc) olan
                  // tedarikçiler listelenir (fbItems backend'den firma-bazlı gelir);
                  // TUMU'da herkes görünür.
                  .filter(t => !firmaAktif || fbItems[t.id] !== undefined)
                  .sort((a, b) => b.bakiye - a.bakiye)
                  .map(t => {
                    // Kolon değerleri: firma görünümünde ledger aggregat'ından
                    // (Ödenen ödemelerden değil — FIFO firma payı), TUMU'da ham listelerden.
                    const tFaturalar = faturalar.filter(f => f.tedarikci_id === t.id);
                    const fb = fbItems[t.id];
                    const toplamFatura = firmaAktif
                      ? (fb ? fb.fatura_toplam || 0 : 0)
                      : tFaturalar.reduce((acc, f) => acc + (f.toplam || f.toplam_tutar || 0), 0);
                    const toplamGider = firmaAktif
                      ? (fb ? fb.gider_toplam || 0 : 0)
                      : giderBorclar.filter(b => b.tedarikci_id === t.id).reduce((acc, b) => acc + (b.orjinal_tutar || 0), 0);
                    const toplamOdeme = firmaAktif
                      ? (fb ? fb.odenen_toplam || 0 : 0)
                      : odemeler.filter(o => o.tedarikci_id === t.id).reduce((acc, o) => acc + (o.tutar || 0), 0);
                    const bs = getBakiyeStyle(t.bakiye);

                    // En yakın vade — vade fatura kaydında, firma görünümünde de o firmanın faturasından
                    const vadeKaynak = firmaAktif ? faturalarFiltered.filter(f => f.tedarikci_id === t.id) : tFaturalar;
                    const vadeler = vadeKaynak.filter(f => f.vade && f.durum !== 'ODENDI').map(f => f.vade).sort();
                    const enYakinVade = vadeler[0];
                    const vadeGecmis = enYakinVade && new Date(enYakinVade) < new Date();

                    return (
                      <tr key={t.id}
                        style={{ ...s.clickableRow, background: t.bakiye > 1000 ? '#fff8f8' : '#fff' }}
                        onClick={() => { setSelectedTedarikci(t.id); setActiveTab('hareketler'); setExpandedRow(null); }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = t.bakiye > 1000 ? '#fff8f8' : '#fff'}
                      >
                        <td style={{ ...s.td, fontWeight: '600' }}>
                          {t.unvan}
                          {t.muhasebe_kodu && (
                            <div style={{ fontSize: '11px', color: colors.textTertiary, fontFamily: 'monospace', marginTop: '2px' }}>{t.muhasebe_kodu}</div>
                          )}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right' }}>{toplamFatura.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                        <td style={{ ...s.td, textAlign: 'right', color: toplamGider > 0 ? '#e65100' : '#999' }}>
                          {toplamGider > 0 ? `${toplamGider.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', color: '#2e7d32' }}>{toplamOdeme.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: '700', color: bs.color }}>
                          {Math.abs(t.bakiye).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </td>
                        <td style={s.td}>
                          <span style={s.badge(bs.bg, bs.color)}>{bs.text}</span>
                        </td>
                        <td style={s.td}>
                          {enYakinVade ? (
                            <span style={{ color: vadeGecmis ? '#d32f2f' : '#666', fontWeight: vadeGecmis ? '600' : '400', fontSize: '13px' }}>
                              {fmtTarih(enYakinVade)} {vadeGecmis ? '⚠' : ''}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CariHesaplarPage;
